/* Loading, error and dialog feedback components shared across the views. */

import { escapeHtml } from '../utils/html.js';
import { logoMark } from './branding.js';

/** Animated ring spinner. `size` is a Tailwind h-/w- utility pair. */
export function spinner(size = 'h-6 w-6') {
  return `
    <svg class="${size} animate-spin text-neutral-900" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <circle class="opacity-20" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="3"></circle>
      <path class="opacity-90" d="M12 2a10 10 0 0 1 10 10" stroke="currentColor" stroke-width="3" stroke-linecap="round"></path>
    </svg>`;
}

/**
 * A centered loading state with the brand mark, spinner and a message.
 * Pass `{ fullscreen: false }` to fill the parent instead of the viewport
 * (e.g. while fetching a chapter's resources into an existing view).
 */
export function loaderView(message = 'Ładowanie…', { fullscreen = true } = {}) {
  const height = fullscreen ? 'min-h-dvh' : 'min-h-40 py-12';
  return `
    <div role="status" aria-live="polite"
      class="flex ${height} flex-col items-center justify-center gap-5 px-6 text-center">
      <div class="flex w-full max-w-sm flex-col items-center gap-3">
        ${logoMark('text-2xl')}
        <div class="mt-2 flex items-center gap-3 text-sm font-medium text-neutral-500">
          ${spinner('h-5 w-5')}
          <span>${escapeHtml(message)}</span>
        </div>
      </div>
    </div>`;
}

/** Replace a mount's contents with the full-screen loader. */
export function showLoader(mount, message = 'Ładowanie…') {
  if (!mount) return;
  mount.className = '';
  mount.innerHTML = loaderView(message);
}

/* -------------------------------------------------------------------------- */
/* Region states — the two cards every list screen needs                      */
/* -------------------------------------------------------------------------- */

/**
 * The "couldn't load this, try again" card that fills a content region.
 *
 * Shared by every list screen (home, chapter, topic, favorites, dictionary),
 * which each had their own byte-identical copy of this markup. The retry control
 * carries a `[data-retry]` hook rather than a per-screen id — {@link wireRetry}
 * is what binds it.
 *
 * @param {?{message?: string}} error The caught error, if any.
 * @param {string} fallback Message to show when the error carries none.
 */
export function errorCard(error, fallback = 'Nie udało się wczytać danych.') {
  const message = error?.message || fallback;
  return `
    <div role="alert" class="flex flex-col items-center gap-4 rounded-2xl bg-red-50 px-6 py-8 text-center">
      <p class="text-sm font-medium text-red-600">${escapeHtml(message)}</p>
      <button data-retry type="button" class="btn-primary px-4 py-2.5">Ponów</button>
    </div>`;
}

/** Bind the retry control of an {@link errorCard} rendered inside `container`. */
export function wireRetry(container, handler) {
  container?.querySelector('[data-retry]')?.addEventListener('click', handler);
}

/**
 * The "there is nothing here" card that fills a content region: a headline and a
 * quiet line of guidance beneath it.
 *
 * @param {string} title Headline (e.g. "Słownik jest pusty").
 * @param {string} [description] Optional second line explaining how to fill it.
 * @param {{ fill?: boolean }} [options]
 *   - fill: centre vertically in a flex-column parent, for a screen whose whole
 *     body is this state. Omit for a state sitting inside a taller page.
 */
export function emptyState(title, description = '', { fill = false } = {}) {
  const box = fill ? 'flex-1 justify-center' : '';
  const hint = description
    ? `<p class="max-w-[15rem] text-sm text-neutral-400">${escapeHtml(description)}</p>`
    : '';
  return `
    <div class="flex ${box} flex-col items-center gap-2 py-10 text-center">
      <p class="text-sm font-medium text-neutral-500">${escapeHtml(title)}</p>
      ${hint}
    </div>`;
}

/** An initially-hidden error alert container. */
export function errorBox(id) {
  return `<div id="${id}" role="alert" hidden class="mt-1 px-1 text-sm text-red-600"></div>`;
}

/**
 * Render (or clear) an API error inside a container.
 * Pass `null` to clear. Accepts an ApiError or any
 * `{ message, violations, retryAfter }`.
 *
 * `violations` matter: on a 422 the backend's `message` is only a generic
 * "Validation failed." and the actual reasons come as a list of strings, so
 * dropping them would leave the user with no idea what was wrong.
 *
 * `retryAfter` (seconds, sent on a 429) adds the "you may send the next one in
 * …" line beneath the message — the backend's own message only says *that* there
 * is a limit, never when it lifts. The line is rendered once here; pass the same
 * seconds to {@link startRetryCountdown} to make it tick.
 */
export function setError(el, error) {
  if (!el) return;
  if (!error) {
    el.hidden = true;
    el.innerHTML = '';
    return;
  }

  const violations = Array.isArray(error.violations) ? error.violations.filter(Boolean) : [];
  const details = violations.length
    ? `<ul class="mt-1 list-disc pl-5">
         ${violations.map((item) => `<li>${escapeHtml(violationText(item))}</li>`).join('')}
       </ul>`
    : '';

  el.hidden = false;
  if(error.retryAfter > 0) {
        el.innerHTML = `<p class="font-medium" data-retry-countdown>${escapeHtml(retryText(error.retryAfter))}</p>`;
        return;
  }

  el.innerHTML = `<p class="font-medium">${escapeHtml(error.message)}</p>${details}`;
}

/* -------------------------------------------------------------------------- */
/* Rate limiting (429)                                                        */
/* -------------------------------------------------------------------------- */

/**
 * How long is left, the way the message says it: "1 min. 23 sek." from a minute
 * up (seconds zero-padded, so the line doesn't jump width while it ticks), bare
 * seconds below one — nobody needs to read "0 min.".
 * @param {number} seconds
 */
export function formatRetryAfter(seconds) {
  const total = Math.max(0, Math.ceil(seconds));
  if (total < 60) return `${total} sek.`;
  return `${Math.floor(total / 60)} min. ${String(total % 60).padStart(2, '0')} sek.`;
}

const retryText = (seconds) => `Kolejny kod możesz wysłać za ${formatRetryAfter(seconds)}`;

/**
 * Tick the countdown line {@link setError} rendered inside `el` down to zero and
 * fire `onExpire` when the limit lifts.
 *
 * Counts from a fixed deadline rather than by decrementing, so a throttled
 * background tab resumes with the right number instead of a stale one. Returns a
 * **cancel** function, and calling it is mandatory: the router replaces the whole
 * mount, so a view that navigates away without cancelling leaves an interval
 * running against a detached element forever.
 *
 * @param {?HTMLElement} el Container previously filled by {@link setError}.
 * @param {number} seconds Seconds left, as the backend reported them.
 * @param {{ onExpire?: () => void }} [options]
 * @returns {() => void} Cancel the countdown.
 */
export function startRetryCountdown(el, seconds, { onExpire } = {}) {
  const line = el?.querySelector('[data-retry-countdown]');
  if (!line) return () => {};

  const deadline = Date.now() + Math.max(0, Math.ceil(seconds)) * 1000;
  let timer = null;
  const stop = () => {
    if (timer !== null) clearInterval(timer);
    timer = null;
  };

  timer = setInterval(() => {
    const left = Math.ceil((deadline - Date.now()) / 1000);
    if (left <= 0) {
      stop();
      onExpire?.();
      return;
    }
    line.textContent = retryText(left);
  }, 1000);

  return stop;
}

/**
 * One violation as text. The contract is a plain string; the object fallbacks are
 * there so a backend that starts sending structured violations degrades to
 * something readable instead of "[object Object]".
 */
function violationText(item) {
  if (typeof item === 'string') return item;
  return item?.message ?? item?.title ?? item?.propertyPath ?? '';
}

/**
 * iOS-style confirmation alert. Renders a centered card with a Cancel/confirm
 * pair and resolves to `true` (confirmed) or `false` (cancelled/dismissed).
 * @param {{ title: string, message?: string, confirmLabel?: string,
 *           cancelLabel?: string, destructive?: boolean }} options
 * @returns {Promise<boolean>}
 */
export function confirmDialog({
  title,
  message = '',
  confirmLabel = 'OK',
  cancelLabel = 'Anuluj',
  destructive = false,
} = {}) {
  return new Promise((resolve) => {
    const overlay = document.createElement('div');
    overlay.className =
      'fixed inset-0 z-[60] flex items-center justify-center px-8 opacity-0 transition-opacity duration-150 data-[open=true]:opacity-100';
    overlay.innerHTML = `
      <div class="absolute inset-0 bg-neutral-900/40 backdrop-blur-sm" data-cancel></div>
      <div role="alertdialog" aria-modal="true" aria-labelledby="cd-title"
        class="relative w-full max-w-[17rem] scale-95 overflow-hidden rounded-2xl bg-white/95 text-center shadow-soft-lg backdrop-blur transition-transform duration-150 data-[open=true]:scale-100"
        data-card>
        <div class="px-5 pb-4 pt-5">
          <h2 id="cd-title" class="text-base font-semibold text-neutral-900">${escapeHtml(title)}</h2>
          ${message ? `<p class="mt-1 text-sm leading-snug text-neutral-500">${escapeHtml(message)}</p>` : ''}
        </div>
        <div class="grid grid-cols-2 divide-x divide-neutral-200/80 border-t border-neutral-200/80 text-base">
          <button type="button" data-cancel class="px-4 py-3 font-medium text-indigo-700 transition active:bg-neutral-100">${escapeHtml(cancelLabel)}</button>
          <button type="button" data-confirm class="px-4 py-3 font-semibold ${destructive ? 'text-red-600' : 'text-indigo-700'} transition active:bg-neutral-100">${escapeHtml(confirmLabel)}</button>
        </div>
      </div>`;

    const root = document.documentElement;
    const wasLocked = root.classList.contains('overflow-hidden');
    root.classList.add('overflow-hidden');

    const close = (result) => {
      document.removeEventListener('keydown', onKey);
      if (!wasLocked) root.classList.remove('overflow-hidden');
      overlay.dataset.open = 'false';
      overlay.querySelector('[data-card]').dataset.open = 'false';
      setTimeout(() => overlay.remove(), 150);
      resolve(result);
    };
    const onKey = (event) => {
      if (event.key === 'Escape') close(false);
    };

    overlay.addEventListener('click', (event) => {
      if (event.target.closest('[data-confirm]')) close(true);
      else if (event.target.closest('[data-cancel]')) close(false);
    });
    document.addEventListener('keydown', onKey);

    document.body.appendChild(overlay);
    // Next frame → trigger the enter transition and focus the confirm button.
    requestAnimationFrame(() => {
      overlay.dataset.open = 'true';
      overlay.querySelector('[data-card]').dataset.open = 'true';
      overlay.querySelector('[data-confirm]').focus();
    });
  });
}

/** Toggle a submit button's loading state (disables it and swaps its label). */
export function setLoading(button, isLoading, loadingText) {
  if (!button) return;
  const label = button.querySelector('.submit-label');
  button.disabled = isLoading;
  if (!label) return;
  if (isLoading) {
    button.dataset.idleLabel = label.textContent;
    label.textContent = loadingText;
  } else if (button.dataset.idleLabel) {
    label.textContent = button.dataset.idleLabel;
  }
}
